import { useState } from 'react'
import { Row, Col, Button } from 'reactstrap'
import s from './AddDealForm.scss'
import cn from 'classnames'
import { KeyValuePairs, GoogleAutocomplete, InputContainer } from 'components/ideal-core-ui'
import { CompanyClients } from 'components/common/CompanyClients'
import { makeGetRequest, makePostRequest } from 'utils/api'
import { fromContacts } from './serializers'
import differenceBy from 'lodash/differenceBy'
import update from 'immutability-helper'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { useImmer } from 'use-immer'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { timezoneDate, utcDate } from 'utils/helpers'
import { getDateFormat } from 'utils/authService'
import DatePicker from 'react-datepicker'
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css' // eslint-disable-line
import moment from 'moment'

const initialContact = {
  firstName: '',
  lastName: '',
  email: '',
  company: ''
}

const initialCompany = {
  name: '',
  legalName: ''
}

const AddDealForm = ({ selectedDeal, setSelectedDeal }) => {
  const [contact, setContact] = useImmer(initialContact)
  const [company, setCompany] = useImmer(initialCompany)
  const [key, setKey] = useState(0)

  const changeProperty = (value, property) => {
    setSelectedDeal(draft => {
      if (draft) {
        draft.value[property] = value
        draft.type = 'new'
      } else {
        return {
          value: {
            [property]: value
          },
          type: 'new'
        }
      }
    })
  }

  const changeNewContactProperty = (value, property) => {
    setContact(draft => {
      draft[property] = value
    })
  }

  const changeCompanyProperty = (value, property) => {
    setCompany(draft => {
      draft[property] = value
    })
  }

  const onSelectCallback = params => {
    const { address, city, state } = params
    setSelectedDeal(draft => {
      if (draft) {
        draft.value.address = address
        draft.value.city = city
        draft.value.state = state
      } else {
        return {
          type: 'new',
          value: {
            address,
            city,
            state
          }
        }
      }
    })
  }

  const changeClientContacts = async value => {
    let state
    if (!value.length) {
      setSelectedDeal(draft => {
        return {
          ...draft,
          type: 'new',
          value: {
            ...draft.value,
            clients: []
          }
        }
      })
      return
    }

    const { value: id, label } = value.slice(-1)[0]
    const url = `/v1/contacts/company/${id}`
    const contacts = await makeGetRequest(url)
    const client = {
      value: id,
      label,
      contacts: fromContacts(contacts)
    }

    if (!selectedDeal?.value.clients) {
      setSelectedDeal(draft => {
        if (draft) {
          return {
            ...draft,
            type: 'new',
            value: {
              ...draft.value,
              clients: [client]
            }
          }
        } else {
          return {
            type: 'new',
            value: {
              clients: [client]
            }
          }
        }
      })
      return
    } else {
      if (value.length < selectedDeal.value.clients.length) {
        const removedClient = differenceBy(selectedDeal.value.clients, value, 'value')
        const index = selectedDeal.value.clients.findIndex(
          client => client.value === removedClient[0].value
        )
        state = update(selectedDeal, {
          value: {
            clients: {
              $splice: [[index, 1]]
            }
          }
        })
      } else {
        state = update(selectedDeal, {
          value: {
            clients: {
              $push: [client]
            }
          }
        })
      }
    }

    setSelectedDeal(() => state)
  }

  const changeClientProperty = (value, property, id) => {
    const index = selectedDeal.value.clients.findIndex(c => c.value === id)
    const state = update(selectedDeal, {
      value: {
        clients: {
          [index]: {
            [property]: { $set: value }
          }
        }
      }
    })
    setSelectedDeal(() => state)
  }

  const refreshClientContacts = async id => {
    const url = `/v1/contacts/company/${id}`
    const contacts = await makeGetRequest(url)
    const index = selectedDeal.value.clients.findIndex(c => c.value === id)
    const newState = update(selectedDeal, {
      value: {
        clients: {
          [index]: {
            contacts: { $set: fromContacts(contacts) }
          }
        }
      }
    })

    setSelectedDeal(() => newState)
  }

  const addCompany = async () => {
    try {
      const formData = new FormData()
      formData.append(
        'companyData',
        JSON.stringify({ name: company.name, legalName: company.legalName })
      )

      await makePostRequest('/v1/companies', formData)
      setKey(key => key + 1)
      setCompany(initialCompany)
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  const addContact = async () => {
    try {
      const formData = new FormData()
      const { firstName, lastName, email, company } = contact
      const contactData = {
        ...(email ? { email } : {}),
        ...(firstName ? { firstName } : {}),
        ...(lastName ? { lastName } : {}),
        ...(company ? { companyId: company.value } : {})
      }

      formData.append('contactData', JSON.stringify(contactData))

      await makePostRequest('/v1/contacts', formData)
      setKey(key => key + 1)
      setContact(initialContact)
      // eslint-disable-next-line no-empty
    } catch (e) {}
  }

  return (
    <>
      <Row>
        <Col className={cn(s.col, s.addDeal)} sm="6">
          <KeyValuePairs
            pairs={[
              {
                key: '* Opportunity Name',
                value: (
                  <InputContainer
                    type="text"
                    text={selectedDeal?.value.name}
                    placeholder="Enter Opportunity Name"
                    cb={value => changeProperty(value, 'name')}
                  />
                )
              },
              {
                key: 'Address',
                value: (
                  <GoogleAutocomplete
                    value={selectedDeal?.value.address}
                    onChange={value => changeProperty(value, 'address')}
                    onSelectCallback={onSelectCallback}
                  />
                )
              },
              {
                key: '* City',
                value: (
                  <InputContainer
                    type="text"
                    text={selectedDeal?.value.city}
                    placeholder="Enter City"
                    cb={value => changeProperty(value, 'city')}
                  />
                )
              },
              {
                key: '* State/Province/Region',
                value: (
                  <InputContainer
                    type="text"
                    text={selectedDeal?.value.state}
                    placeholder="Enter State/Province/Region"
                    cb={value => changeProperty(value, 'state')}
                  />
                )
              },
              {
                key: 'Bid Due Date',
                valueClass: [s.dueDateWrapper],
                value: (
                  <DatePicker
                    selected={moment(
                      timezoneDate(selectedDeal?.value.dueDate, 'HH:mm:ss'),
                      `${getDateFormat()} HH:mm:ss`
                    ).toDate()}
                    startDate={moment(
                      timezoneDate(selectedDeal?.value.dueDate, 'HH:mm:ss'),
                      `${getDateFormat()} HH:mm:ss`
                    ).toDate()}
                    onChange={value => {
                      if (!value) return
                      changeProperty(utcDate(value), 'dueDate')
                    }}
                    showTimeSelect
                    timeFormat="h:mm aa"
                    timeIntervals={15}
                    dateFormat={`${getDateFormat()
                      .replace('YYYY', 'yyyy')
                      .replace('DD', 'dd')} h:mm aa`}
                    timeCaption="time"
                    placeholderText="Select deal due date"
                    className={s.dueDate}
                  />
                )
              },
              {
                key: 'Quote Number',
                value: (
                  <InputContainer
                    type="text"
                    text={selectedDeal?.value.quoteNumber}
                    placeholder="Enter Quote Number"
                    cb={value => changeProperty(value, 'quoteNumber')}
                  />
                )
              }
            ]}
          />
          <CompanyClients
            withDates={false}
            defaultOptions
            withContactDetails={false}
            style={{ padding: 0 }}
            entity={selectedDeal?.value || {}}
            clientsSelectClass={s.clientsSelect}
            changeClientContacts={changeClientContacts}
            changeClientProperty={changeClientProperty}
            refreshClientContacts={refreshClientContacts}
            selectKey={key}
          />
        </Col>
        <Col className={s.col} sm="6">
          <KeyValuePairs
            pairs={[
              {
                key: 'Add Contact',
                keyClass: [s.label],
                value: (
                  <>
                    <InputContainer
                      type="text"
                      text={contact.firstName}
                      classname={s.input}
                      placeholder="*Contact First Name"
                      cb={value => changeNewContactProperty(value, 'firstName')}
                    />
                    <InputContainer
                      type="text"
                      text={contact.lastName}
                      classname={s.input}
                      placeholder="*Contact Last Name"
                      cb={value => changeNewContactProperty(value, 'lastName')}
                    />
                    <InputContainer
                      type="text"
                      text={contact.email}
                      classname={s.input}
                      placeholder="*Contact Email"
                      cb={value => changeNewContactProperty(value, 'email')}
                    />
                    <PaginatedSelect
                      url="/v1/companies"
                      className={s.input}
                      value={contact.company}
                      onChange={value => changeNewContactProperty(value, 'company')}
                      serializer={toCompaniesSelect}
                      selectKey={key}
                    />
                    <Button
                      disabled={
                        !contact.firstName ||
                        !contact.lastName ||
                        !contact.email ||
                        !contact.company
                      }
                      className={s.saveBtn}
                      onClick={addContact}
                    >
                      Add Contact
                    </Button>
                  </>
                )
              }
            ]}
          />
          <KeyValuePairs
            pairs={[
              {
                key: 'Add Company',
                keyClass: [s.label],
                value: (
                  <>
                    <InputContainer
                      type="text"
                      text={company.name}
                      classname={s.input}
                      placeholder="*Company Name"
                      cb={value => changeCompanyProperty(value, 'name')}
                    />
                    <InputContainer
                      type="text"
                      text={company.legalName}
                      classname={s.input}
                      placeholder="*Company Legal Name"
                      cb={value => changeCompanyProperty(value, 'legalName')}
                    />
                    <Button
                      disabled={!company.name || !company.legalName}
                      className={s.saveBtn}
                      onClick={addCompany}
                    >
                      Add Company
                    </Button>
                  </>
                )
              }
            ]}
          />
        </Col>
      </Row>
    </>
  )
}

export default AddDealForm
